import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { CacheProvider } from './contexts/CacheContext';
import PrivateRoute from './components/PrivateRoute';
import PermissionRoute from './components/PermissionRoute';
import Layout from './components/Layout';
import LogViewer from './components/LogViewer';
import Spinner from './components/Spinner';
import ProfileCompletionModal from './components/ProfileCompletionModal';
import ErrorBoundary from './components/ErrorBoundary';
import logger from './utils/logger';
import { getOnboardingGate, approvalRequired, GATE_NOTICE } from './utils/onboardingGate';
import './utils/fileLogWriter'; // Initialize server-side file logging only
import './styles/App.css';
import { getTokenRefreshManager } from './services/tokenRefreshManager'; // Import token refresh manager
import { applyIOSFixes } from './utils/iOSKeyboardFix'; // iOS keyboard and viewport fixes
import { isTrustedOrigin } from './config/trustedOrigins'; // postMessage origin allowlist (configurable)
import { isExpectedPopupMessage } from './utils/oauthMessageGuard';

// 🆕 LANDING PAGE - Import landing page styles and smart routing components
import './landing/styles/landing.css';
import SmartRoot from './components/SmartRoot';
import AuthRedirect from './components/AuthRedirect';

// Import frequently accessed user pages directly (no lazy loading = instant navigation, no loader!)
import Chat from './pages/Chat';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Bookings from './pages/Bookings';

// Lazy load admin / management / heavy pages so non-admins don't download the
// entire admin surface on first paint (Suspense fallback below covers them).
const Admin = lazy(() => import('./pages/Admin'));
const LiveConversations = lazy(() => import('./pages/LiveConversations'));
const LocationManagement = lazy(() => import('./pages/LocationManagement'));
const ResourceManagement = lazy(() => import('./pages/ResourceManagement'));
const AmenityManagement = lazy(() => import('./pages/AmenityManagement'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const WorkspaceDocuments = lazy(() => import('./pages/WorkspaceDocuments'));
const WorkspaceFAQs = lazy(() => import('./pages/WorkspaceFAQs'));
const Settings = lazy(() => import('./pages/Settings'));
const GoogleCalendar = lazy(() => import('./pages/GoogleCalendar'));
const Logs = lazy(() => import('./pages/admin/Logs'));
const BulkSMS = lazy(() => import('./pages/admin/BulkSMS'));
const AppSettings = lazy(() => import('./pages/admin/AppSettings'));
const SmsCosts = lazy(() => import('./pages/admin/SmsCosts'));
const LocationContacts = lazy(() => import('./pages/admin/LocationContacts'));
const Community = lazy(() => import('./pages/admin/Community'));
const MessageFeedback = lazy(() => import('./pages/admin/MessageFeedback'));
const ErrorLogs = lazy(() => import('./pages/admin/ErrorLogs'));
const UserRoleAssignment = lazy(() => import('./pages/UserRoleAssignment'));
// Public support ticket page (embedded on kiln.com / member.kiln.com). Self-contained module.
const SupportTicketPage = lazy(() => import('./modules/supportTicket'));
const MemberScorecard = lazy(() => import('./pages/MemberScorecard'));
const DemoFoodOrder = lazy(() => import('./pages/DemoFoodOrder'));
const DemoCafeOrder = lazy(() => import('./pages/DemoCafeOrder'));
const MemberTimelinePage = lazy(() => import('./pages/MemberTimeline'));

// Lazy load ONLY rarely accessed pages (auth, callbacks, etc.)
const Login = lazy(() => import('./pages/Login'));
const Migration = lazy(() => import('./pages/Migration'));
const CalendarCallback = lazy(() => import('./pages/CalendarCallback'));
const CalendarSuccess = lazy(() => import('./pages/CalendarSuccess'));
const CalendarError = lazy(() => import('./pages/CalendarError'));
const CalendarQuickConnect = lazy(() => import('./pages/CalendarQuickConnect'));
const PopupSuccessHandler = lazy(() => import('./pages/PopupSuccessHandler'));
const AdminChatMonitor = lazy(() => import('./components/AdminChatMonitor'));
const PendingApproval = lazy(() => import('./pages/PendingApproval'));
const PrivacyPolicy = lazy(() => import('./landing/pages/PrivacyPolicy'));
// const Calendar = lazy(() => import('./landing/pages/Calendar'));

// Wrapper component to handle profile completion modal
function AppContent() {
  const [showLogs] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const isDevelopment = process.env.NODE_ENV === 'development';
  const location = useLocation();
  const { user, isAuthenticated, checkAuth, profileStatus } = useAuth();

  // Check if profile completion modal should show after login
  useEffect(() => {
    // Only check after successful login (when on protected routes)
    // Skip check if user is not authenticated or already on auth/pending pages
    if (!isAuthenticated || !user) return;
    if (['/', '/login', '/register', '/pending-approval'].includes(location.pathname)) return;

    // Check profile completion status
    const hasPhone = user.phone_number && user.phone_number.length > 0;
    const hasSmsConsent = user.has_sms_consent === true;
    // Photo is optional during registration (see PHOTO_ENABLED in
    // ProfileCompletionModal.js). Do NOT gate "profile complete" on the image,
    // otherwise pending users without a photo never reach /pending-approval.
    const isProfileComplete = hasPhone && hasSmsConsent;
    const isPending = user.status === 'pending';

    // Redirect pending users with complete profile to pending approval page —
    // unless the onboarding gate turned admin approval off (A/B flow), in
    // which case verified users stay in the app.
    const gate = getOnboardingGate(user, profileStatus);
    if (isProfileComplete && isPending && approvalRequired(gate)) {
      logger.info('APP', 'User profile complete but pending approval, redirecting', {
        status: user.status,
        currentPath: location.pathname
      });
      window.location.href = '/pending-approval';
      return;
    }

    // Show profile completion modal if needed. shouldShowModal honors the
    // backend's profile_status.bypass_required flag — approved users with an
    // unverified phone never see the modal (phone-verification spec §4).
    if (ProfileCompletionModal.shouldShowModal(user, profileStatus)) {
      setShowProfileModal(true);
      ProfileCompletionModal.markAsShown(user);
      logger.info('APP', 'Showing profile completion modal', {
        hasPhone: !!user.phone_number,
        hasImage: !!user.user_image,
        hasSmsConsent: !!user.has_sms_consent,
        bypassRequired: !!profileStatus?.bypass_required
      });
    } else {
      setShowProfileModal(false);
    }
  }, [isAuthenticated, user, location.pathname, profileStatus]);

  useEffect(() => {
    logger.info('APP', 'App component mounted', { isDevelopment });

    // Apply iOS Safari fixes for keyboard and viewport issues (iPhone 14 Pro compatibility)
    applyIOSFixes();

    // Initialize TokenRefreshManager for proactive token refresh
    getTokenRefreshManager();
    logger.info('APP', 'TokenRefreshManager initialized for 7-day session persistence');

    // Handle OAuth messages when app is in iframe mode
    const handleOAuthMessage = (event) => {
      // Reject messages from untrusted origins before logging/acting on them.
      if (!isTrustedOrigin(event.origin)) {
        return;
      }

      // Log trusted messages for debugging
      logger.info('APP', 'Message received in iframe', {
        origin: event.origin,
        data: event.data,
        isInIframe: window.self !== window.top
      });

      // Check if we're in an iframe
      if (window.self !== window.top) {
        // Only act on messages from the popup this window opened (L10)
        if (!isExpectedPopupMessage(event.data)) {
          return;
        }
        // Handle Google Calendar OAuth success messages
        if (event.data?.type === 'google_calendar_oauth_result' || 
            event.data?.action === 'calendar_connected' ||
            event.data?.type === 'oauth_success') {
          
          logger.info('APP', 'OAuth success detected in iframe', event.data);
          
          // Reload the iframe to refresh the calendar list
          if (event.data.success || event.data.action === 'calendar_connected') {
            logger.info('APP', 'Reloading iframe after calendar connection...');
            
            // Small delay to ensure backend has processed the connection
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }
        
        // Also handle messages from the OAuth popup window
        if (event.data?.type === 'oauth_callback' && event.data?.provider === 'google') {
          logger.info('APP', 'OAuth callback from popup', event.data);
          
          if (event.data.success) {
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }
      }
    };

    // Listen for postMessage events
    window.addEventListener('message', handleOAuthMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleOAuthMessage);
    };
  }, [isDevelopment]);

  const handleProfileSave = async () => {

logger.debug('APP', '=== App: handleProfileSave called ===');
    // Refresh user data after profile update
    if (checkAuth) {

logger.debug('APP', 'Refreshing user data...');
      await checkAuth();

logger.debug('APP', 'User data refreshed');
    }
    setShowProfileModal(false);

logger.debug('APP', 'Modal closed');
  };

  return (
    <>
      <Toaster position="top-right" />
      <Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <Spinner size="lg" centered text="Loading page..." />
        </div>
      }>
              <Routes>
          {/* 🆕 SMART ROOT ROUTE - Shows landing page OR redirects to dashboard based on auth status */}
          <Route path="/" element={<SmartRoot />} />

          {/* Public routes - Redirect to /chat if already authenticated */}
          <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
          {/* <Route path="/register" element={<AuthRedirect><Register /></AuthRedirect>} /> */}

          {/* Privacy Policy - Public page */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Public support ticket form - no login, see src/modules/supportTicket/README.md */}
          <Route path="/support-ticket" element={<SupportTicketPage />} />

          {/* Calendar & AI Transparency - Public page */}
          {/* <Route path="/calendar" element={<Calendar />} /> */}

          {/* Pending Approval Page - Accessible when authenticated but pending */}
          <Route path="/pending-approval" element={<PendingApproval />} />

          {/* OAuth callback routes - need to be outside protected routes */}
          <Route path="/calendar/quick-connect" element={<CalendarQuickConnect />} />
          <Route path="/calendar/callback" element={<CalendarCallback />} />
          <Route path="/calendar/success" element={<CalendarSuccess />} />
          <Route path="/calendar/error" element={<CalendarError />} />

          {/* Handle malformed URLs from backend */}
          <Route path="/calendar/callback/calendar/success" element={<PopupSuccessHandler />} />
          <Route path="/calendar/callback/calendar/error" element={<CalendarError />} />

          {/* Handle OAuth popup callbacks */}
          <Route path="/calendar/callback/popup" element={<PopupSuccessHandler />} />
          <Route path="/api/google-calendar/callback/popup" element={<PopupSuccessHandler />} />

          {/* Protected routes - APP PAGES */}
          <Route element={<PrivateRoute />}>
            <Route element={<Layout />}>
              {/* Dashboard: analytics:view was renamed to dashboard:view, after
                  the screen it guards. Same permission the API and the sidebar
                  use, so all three layers agree. */}
              <Route element={<PermissionRoute resource="dashboard" action="view" />}>
                <Route path="/dashboard" element={<Dashboard />} />
              </Route>
              <Route path="/chat" element={<Chat />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/google-calendar" element={<GoogleCalendar />} />
              <Route element={<PermissionRoute resource="users" action="read" />}>
                <Route path="/admin" element={<Admin />} />
              </Route>
              {/* Member Scorecard runs on mock data and members:scorecard is
                  seeded to super_admin only, so access is unchanged — but it is
                  a permission now, not a hardcoded role string, so it can be
                  granted without editing code. */}
              <Route element={<PermissionRoute resource="members" action="scorecard" />}>
                <Route path="/members/scorecard" element={<MemberScorecard />} />
              </Route>
              {/* Client demos of ordering food (Uber Eats + Link) and from the
                  building café (Square sandbox). Role-gated, not a permission: they
                  must never be grantable to other roles by accident. */}
              <Route element={<PermissionRoute requireRole="super_admin" />}>
                <Route path="/demo/food-order" element={<DemoFoodOrder />} />
                <Route path="/demo/cafe-order" element={<DemoCafeOrder />} />
              </Route>

              {/* One member's journey. Opens in its own tab from the scorecard, Live Conversations
                  and the Users page. Its own permission group (timeline:*) — see the backend catalogue. */}
              <Route element={<PermissionRoute resource="timeline" action="view" />}>
                <Route path="/members/:id/timeline" element={<MemberTimelinePage />} />
              </Route>
              {/* Reads a member's conversations, so it needs what the API
                  needs. users:read here meant the page opened and then 403'd. */}
              <Route element={<PermissionRoute resource="messages" action="read_all" />}>
                <Route path="/admin/users/:userId/chat-monitor" element={<AdminChatMonitor />} />
              </Route>
              <Route element={<PermissionRoute resource="users" action="manage_roles" />}>
                <Route path="/admin/users/:userId/roles" element={<UserRoleAssignment />} />
              </Route>
              <Route element={<PermissionRoute resource="logs" action="read" />}>
                <Route path="/admin/logs" element={<Logs />} />
              </Route>
              <Route element={<PermissionRoute resource="roles" action="read" />}>
                <Route path="/admin/roles" element={<RoleManagement />} />
              </Route>
              <Route element={<PermissionRoute resource="locations" action="read" />}>
                <Route path="/admin/locations" element={<LocationManagement />} />
                <Route path="/admin/locations/contacts" element={<LocationContacts />} />
              </Route>
              <Route element={<PermissionRoute resource="community" action="read" />}>
                <Route path="/admin/community" element={<Community />} />
              </Route>
              {/* SMS Costs used to sit in the Community block above — a
                  copy-paste that made it unreachable for the people who had
                  analytics access and reachable-but-broken for those who did
                  not. It is member spend data, so it has its own permission. */}
              <Route element={<PermissionRoute resource="sms_costs" action="read" />}>
                <Route path="/admin/sms-costs" element={<SmsCosts />} />
              </Route>
              <Route element={<PermissionRoute resource="resources" action="read" />}>
                <Route path="/admin/resources" element={<ResourceManagement />} />
              </Route>
              <Route element={<PermissionRoute resource="amenities" action="read" />}>
                <Route path="/admin/amenities" element={<AmenityManagement />} />
              </Route>
              <Route element={<PermissionRoute resource="bulk_sms" action="read" />}>
                <Route path="/admin/bulk-sms" element={<BulkSMS />} />
              </Route>
              <Route element={<PermissionRoute resource="feedback" action="read" />}>
                <Route path="/admin/feedback" element={<MessageFeedback />} />
              </Route>
              {/* Errors is its own screen. It used to borrow feedback:read, so
                  granting Message Feedback silently granted Errors too. */}
              <Route element={<PermissionRoute resource="errors" action="read" />}>
                <Route path="/admin/errors" element={<ErrorLogs />} />
              </Route>
              <Route element={<PermissionRoute resource="conversations" action="view_live" />}>
                <Route path="/admin/live-conversations" element={<LiveConversations />} />
              </Route>
              {/* The legacy "documents" permission was retired in favour of
                  "faqs"; this page had no guard at all. Kept, not deleted. */}
              <Route element={<PermissionRoute resource="faqs" action="read" />}>
                <Route path="/workspace-documents" element={<WorkspaceDocuments />} />
              </Route>
              <Route element={<PermissionRoute resource="faqs" action="read" />}>
                <Route path="/workspace-faqs" element={<WorkspaceFAQs />} />
              </Route>
              {/* system:admin is the global override — it grants EVERY
                  permission. Gating this raw-data browser on it meant handing
                  out everything with it. */}
              <Route element={<PermissionRoute resource="developer_tools" action="read" />}>
                <Route path="/settings" element={<Settings />} />
              </Route>
              <Route element={<PermissionRoute resource="settings" action="read" />}>
                <Route path="/admin/settings" element={<AppSettings />} />
              </Route>
              <Route element={<PermissionRoute resource="system" action="migrate" />}>
                <Route path="/settings/migration" element={<Migration />} />
              </Route>
            </Route>
          </Route>
          </Routes>
            </Suspense>
            
            {/* Log Viewer - Only in development or when explicitly enabled */}
            {(isDevelopment || showLogs) && showLogs && <LogViewer />}
            
            {/* Development helper button */}
            {/* {isDevelopment && (
              <button
                onClick={() => setShowLogs(!showLogs)}
                style={{
                  position: 'fixed',
                  bottom: '20px',
                  right: '20px',
                  padding: '8px 16px',
                  backgroundColor: showLogs ? '#ef4444' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  zIndex: 9998
                }}
              >
                {showLogs ? 'Hide Logs' : 'Show Logs'}
              </button>
            )} */}
            
            {/* Profile Completion Modal. gateNotice: when the onboarding gate
                (A/B) reopened this modal because the free-message window is
                used up, show the security line — same copy as the in-chat gate. */}
            <ProfileCompletionModal
              isOpen={showProfileModal}
              onClose={() => setShowProfileModal(false)}
              user={user}
              onSave={handleProfileSave}
              gateNotice={
                getOnboardingGate(user, profileStatus)?.verification_required === true
                  ? GATE_NOTICE
                  : null
              }
            />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <CacheProvider>
            <PermissionProvider>
              <AppContent />
            </PermissionProvider>
          </CacheProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;